// -----------------------
// TODO: split this into many files
// -----------------------
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { useSelector } from 'react-redux';
import { Point } from '@printu/geom';
import { generateFancyArrowPoints, generateSimpleArrowShape, } from 'app/core/graphics/Arrow';
import { getItemAbsoluteBox, getItemBounds } from 'app/core/markers/bounds';
import { getTextFormat } from 'app/core/markers/models';
import { getProjectState } from 'app/stores/project';
import { applyCrop, pointsToPathData, svgPoint } from 'app/utils/geom';
import { SVG } from '../../svg/SVG';
import { getMarkerStyle } from './style';
import { TextMarkerRenderer } from './Text';
import { castedProps } from './types';
const ShapeSVG = (props) => {
    const { shape } = props;
    return (React.createElement(React.Fragment, null, shape.map(item => {
        switch (item.type) {
            case 'line':
                return (React.createElement("line", { key: item.uid, x1: item.start.x, y1: item.start.y, x2: item.end.x, y2: item.end.y, strokeLinecap: "round", strokeLinejoin: "round" }));
            case 'polyline':
                return (React.createElement("polyline", { key: item.uid, points: item.points
                        .map(point => svgPoint(point))
                        .join(', '), strokeLinecap: "round", strokeLinejoin: "round" }));
            default:
                return null;
        }
    })));
};
const Line = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const style = getMarkerStyle(marker, 'stroke', opts);
    const { endX, endY } = marker;
    return (React.createElement("g", { transform: `translate(${marker.x},${marker.y})` },
        React.createElement("line", Object.assign({ x2: endX, y2: endY, strokeLinecap: "round" }, style.secondary)),
        React.createElement("line", Object.assign({ x2: endX, y2: endY, strokeLinecap: "round" }, style.primary))));
};
const Polyline = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const style = getMarkerStyle(marker, 'stroke', opts);
    const { points } = marker;
    const pointsStr = `0,0 ${points.map(({ x, y }) => `${x},${y}`).join(' ')}`;
    const Tag = marker.closed ? 'polygon' : 'polyline';
    return (React.createElement("g", { transform: `translate(${marker.x},${marker.y})` },
        React.createElement(Tag, Object.assign({ points: pointsStr, strokeLinecap: "round", strokeLinejoin: "round" }, style.secondary)),
        React.createElement(Tag, Object.assign({ points: pointsStr, strokeLinecap: "round", strokeLinejoin: "round" }, style.primary))));
};
const Arrow = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const { size = 1 } = marker;
    const startPoint = new Point(marker.x, marker.y);
    const endPoint = new Point(marker.x + marker.endX, marker.y + marker.endY);
    const width = 1.5 * size;
    switch (marker.type) {
        case 'fancy': {
            const points = generateFancyArrowPoints(startPoint, endPoint, width);
            const [anchor, path] = pointsToPathData(points);
            const style = getMarkerStyle(marker, 'fill', opts);
            return (React.createElement("g", { transform: `translate(${anchor.x},${anchor.y})` },
                React.createElement("path", Object.assign({}, style.secondary, { d: path, strokeLinecap: "round", strokeLinejoin: "round" })),
                React.createElement("path", Object.assign({}, style.primary, { d: path, strokeLinecap: "round", strokeLinejoin: "round", stroke: "none" }))));
        }
        case 'simple': {
            const id = `arrow-${marker.id}`;
            const idRef = `#${id}`;
            const lineShape = generateSimpleArrowShape(startPoint, endPoint, width);
            const style = getMarkerStyle(marker, 'stroke', opts);
            return (React.createElement("g", null,
                React.createElement("defs", null,
                    React.createElement("g", { id: id }, lineShape && React.createElement(ShapeSVG, { shape: lineShape }))),
                React.createElement("g", null,
                    React.createElement("use", Object.assign({ href: idRef }, style.secondary)),
                    React.createElement("use", Object.assign({ href: idRef }, style.primary)))));
        }
        default:
            return null;
    }
};
const Rect = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const bounds = getItemAbsoluteBox(marker);
    const style = getMarkerStyle(marker, 'stroke', opts);
    return (React.createElement("g", { transform: `translate(${bounds.x},${bounds.y})` },
        React.createElement("rect", Object.assign({ width: bounds.width, height: bounds.height }, style.secondary)),
        React.createElement("rect", Object.assign({ width: bounds.width, height: bounds.height }, style.primary))));
};
const Ellipse = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const style = getMarkerStyle(marker, 'stroke', opts);
    const bounds = getItemAbsoluteBox(marker);
    const rx = bounds.size.width * 0.5;
    const ry = bounds.size.height * 0.5;
    return (React.createElement("g", { transform: `translate(${bounds.x}, ${bounds.y})` },
        React.createElement("ellipse", Object.assign({ cx: rx, cy: ry, rx: rx, ry: ry }, style.secondary)),
        React.createElement("ellipse", Object.assign({ cx: rx, cy: ry, rx: rx, ry: ry }, style.primary))));
};
const Path = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const style = getMarkerStyle(marker, 'stroke', opts);
    return (React.createElement("g", { transform: `translate(${marker.x}, ${marker.y})` },
        React.createElement("path", Object.assign({ d: marker.data, strokeLinecap: "round", strokeLinejoin: "round" }, style.secondary)),
        React.createElement("path", Object.assign({ d: marker.data, strokeLinecap: "round", strokeLinejoin: "round" }, style.primary))));
};
const Highlight = (props) => {
    const _a = castedProps(props), { marker } = _a, _opts = __rest(_a, ["marker"]);
    return (React.createElement("g", { transform: `translate(${marker.x}, ${marker.y})` },
        React.createElement("path", { d: marker.data, strokeLinecap: "round", strokeLinejoin: "round", fill: "none", stroke: marker.color, strokeWidth: marker.size.toFixed(0), strokeOpacity: 0.35 })));
};
const Zoom = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const clipId = `clip-${marker.id}`;
    const { x, y, endX, endY, width, height, zoom } = marker;
    const widthHalf = width * 0.5;
    const heightHalf = height * 0.5;
    const zoomCenterX = x + endX + widthHalf;
    const zoomCenterY = y + endY + heightHalf;
    const style = getMarkerStyle(marker, 'stroke', opts);
    // let vec = Point.from(endX, endY);
    // const vecLength = vec.length;
    // const maxLengthAdjust = vecLength * 0.05;
    // const adjustSize = Math.min(
    //     marker.size,
    //     vecLength * 0.01 + Math.min(radius / 90, 1) * maxLengthAdjust
    // );
    // vec = vec.normalize(adjustSize);
    const vec = Point.from(0, 0);
    switch (marker.type) {
        case 'rect': {
            return (React.createElement("g", null,
                React.createElement("g", { transform: `translate(${x}, ${y})` },
                    React.createElement("g", null,
                        React.createElement("line", Object.assign({ x1: vec.x, y1: vec.y, x2: endX, y2: endY, strokeLinecap: "round" }, style.secondary)),
                        React.createElement("rect", Object.assign({ x: endX - widthHalf, y: endY - heightHalf, width: width, height: height }, style.secondary))),
                    React.createElement("g", null,
                        React.createElement("line", Object.assign({ x1: vec.x, y1: vec.y, x2: endX, y2: endY, strokeLinecap: "round" }, style.primary)),
                        React.createElement("rect", Object.assign({ x: endX - widthHalf, y: endY - heightHalf, width: width, height: height }, style.primary)))),
                React.createElement("g", { transform: `translate(${zoomCenterX}, ${zoomCenterY})` },
                    React.createElement("defs", null,
                        React.createElement("clipPath", { id: clipId },
                            React.createElement("rect", { x: -widthHalf, y: -heightHalf, width: width, height: height }))),
                    React.createElement("g", { clipPath: `url(#${clipId})`, transform: `translate(${-widthHalf}, ${-heightHalf})` },
                        React.createElement("use", { href: "#image", transform: `scale(${zoom}, ${zoom}) translate(${-marker.x}, ${-marker.y})` })))));
        }
        case 'ellipse':
        default: {
            const radius = Math.min(widthHalf, heightHalf);
            const radius2 = radius * 1.005;
            return (React.createElement("g", null,
                React.createElement("g", { transform: `translate(${x}, ${y})` },
                    React.createElement("g", null,
                        React.createElement("line", Object.assign({ x1: vec.x, y1: vec.y, x2: endX, y2: endY, strokeLinecap: "round" }, style.secondary)),
                        React.createElement("circle", Object.assign({ cx: endX, cy: endY, r: radius2 }, style.secondary))),
                    React.createElement("g", null,
                        React.createElement("line", Object.assign({ x1: vec.x, y1: vec.y, x2: endX, y2: endY, strokeLinecap: "round" }, style.primary)),
                        React.createElement("circle", Object.assign({ cx: endX, cy: endY, r: radius2 }, style.primary)))),
                React.createElement("g", { transform: `translate(${zoomCenterX}, ${zoomCenterY})` },
                    React.createElement("defs", null,
                        React.createElement("clipPath", { id: clipId },
                            React.createElement("circle", { cx: 0, cy: 0, r: radius }))),
                    React.createElement("g", { clipPath: `url(#${clipId})`, transform: `translate(${-widthHalf}, ${-heightHalf})` },
                        React.createElement("use", { href: "#image", transform: `scale(${zoom}, ${zoom}) translate(${-marker.x}, ${-marker.y})` })))));
        }
    }
};
const Blur = (props) => {
    const useSolidBlur = true;
    const _a = castedProps(props), { marker } = _a, _opts = __rest(_a, ["marker"]);
    const clipId = `clip-${marker.id}`;
    const bounds = getItemAbsoluteBox(marker);
    if (useSolidBlur) {
        return React.createElement("rect", Object.assign({}, bounds.toObject(), { stroke: "none", fill: "black" }));
    }
    // by default Blur should be use in some kind of an parent renderer
    // where pixelated image is shared across many blurs (see. Project.tsx#Blurs)
    return (React.createElement("g", { transform: `translate(${bounds.x}, ${bounds.y})` },
        React.createElement("defs", null,
            React.createElement("clipPath", { id: clipId },
                React.createElement("rect", { x: 0, y: 0, width: bounds.width, height: bounds.height }))),
        React.createElement("g", { clipPath: `url(#${clipId})` },
            React.createElement("use", { href: "#thumbnail", width: "100%", height: "100%", transform: `translate(${-bounds.x}, ${-bounds.y})` }))));
};
const Spot = (props) => {
    const _a = castedProps(props), { marker } = _a, _opts = __rest(_a, ["marker"]);
    const bounds = getItemAbsoluteBox(marker);
    switch (marker.type) {
        case 'ellipse': {
            const rx = bounds.size.width * 0.5;
            const ry = bounds.size.height * 0.5;
            return (React.createElement("ellipse", { cx: bounds.x + rx, cy: bounds.y + ry, rx: rx, ry: ry, fill: "black" }));
        }
        case 'rect':
            return React.createElement("rect", Object.assign({}, bounds.toObject(), { fill: "black" }));
        default:
            return null;
    }
};
const Pin = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const { width, height, innerWidth, innerHeight, descent } = marker;
    const format = getTextFormat(marker);
    const style = getMarkerStyle(marker, 'fill', opts);
    const radius = Math.max(width, height);
    const fitSquareSize = radius * Math.SQRT2;
    const scale = fitSquareSize / Math.max(innerWidth, innerHeight);
    return (React.createElement("g", null,
        React.createElement("g", { transform: `translate(${marker.x}, ${marker.y})` },
            React.createElement("circle", Object.assign({ cx: 0, cy: 0, r: radius }, style.secondary)),
            React.createElement("circle", Object.assign({ cx: 0, cy: 0, r: radius }, style.primary)),
            React.createElement("g", { transform: `scale(${scale}, ${scale})` },
                React.createElement("g", { transform: `translate(${-innerWidth * 0.5}, ${innerHeight * 0.5 - descent})` },
                    React.createElement("text", { fontFamily: format.fontFamily, fontSize: `${format.fontSize}${format.metrics}`, stroke: "none", fill: "#FFFFFF" },
                        React.createElement("tspan", { lengthAdjust: "spacingAndGlyphs" }, marker.number)))))));
};
const Image = (props) => {
    const _a = castedProps(props), { marker } = _a, opts = __rest(_a, ["marker"]);
    const image = useSelector((state) => getProjectState(state).images[marker.image]);
    const style = getMarkerStyle(marker, 'stroke', opts);
    const bounds = getItemBounds(marker);
    const markerWidth = Math.max(bounds.width, 50);
    const markerHeight = Math.max(bounds.height, 50);
    let imageRect = image
        ? {
            x: 0,
            y: 0,
            width: image.size.width,
            height: image.size.height,
        }
        : undefined;
    if (marker.crop) {
        imageRect = applyCrop(imageRect, marker.crop);
    }
    return (React.createElement("g", null,
        React.createElement("g", { transform: `translate(${marker.x}, ${marker.y})` },
            React.createElement("rect", { width: markerWidth, height: markerHeight, fill: "black", fillOpacity: 0.5 }),
            React.createElement("g", null,
                React.createElement("rect", Object.assign({ width: markerWidth, height: markerHeight }, style.secondary)),
                React.createElement("rect", Object.assign({ width: markerWidth, height: markerHeight }, style.primary))),
            image && (React.createElement(SVG, { width: markerWidth, height: markerHeight, preserveAspectRatio: "none", viewBox: `${imageRect.x} ${imageRect.y} ${imageRect.width} ${imageRect.height}` },
                React.createElement("image", { id: "image", preserveAspectRatio: "none", href: image.url, width: image.size.width, height: image.size.height }))))));
};
const map = {
    Arrow,
    Line,
    Polyline,
    Ellipse,
    Rect,
    Path,
    Highlight,
    Blur,
    Spot,
    Zoom,
    Pin,
    Text: TextMarkerRenderer,
    Image,
};
export const Marker = (props) => {
    const { id, props: innerProps } = props;
    const marker = useSelector((state) => getProjectState(state).markers[id]);
    const Renderer = map[marker.alias];
    if (!Renderer) {
        return null;
    }
    const useStroke = configGet('useWhiteStroke', true);
    return React.createElement(Renderer, Object.assign({ useStroke: useStroke }, innerProps, { marker: marker }));
};
